import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCcw, Search } from "lucide-react";

import { AppSidebar } from "@/components/sidebar/app-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  GeoPipelineRun,
  GeoStats,
  GeocodeCacheItem,
  GeocodeCacheStatus,
  getGeoStats,
  listGeocodeCache,
} from "@/services/geoApi";

function pct(n: number, d: number): string {
  if (!d) return "—";
  return `${Math.round((n / d) * 100)}%`;
}

function fmtCoord(v: number | null): string {
  return v == null ? "—" : v.toFixed(4);
}

/** Status with fallback for a backoffice that predates the `status` field. */
function itemStatus(it: GeocodeCacheItem): GeocodeCacheStatus {
  if (it.status) return it.status;
  if (it.resolved) return "resolved";
  return it.country ? "country_only" : "miss";
}

const STATUS_LABEL: Record<GeocodeCacheStatus, string> = {
  resolved: "résolu",
  country_only: "pays seul",
  miss: "sans résultat",
};

const STATUS_VARIANT: Record<GeocodeCacheStatus, "default" | "outline" | "secondary"> = {
  resolved: "default",
  country_only: "outline",
  miss: "secondary",
};

/** One heartbeat line of the resolution pipeline. "aucun signal" / "en
 * retard" are the states that used to be invisible: a run that never
 * happens, or one that exits 0 without doing anything, looks identical to a
 * healthy night from the K8s side. */
function PipelineRow({
  label,
  run,
  staleHours,
  detail,
}: {
  label: string;
  run: GeoPipelineRun | null | undefined;
  staleHours: number;
  detail: (r: GeoPipelineRun) => string;
}) {
  if (!run?.at) {
    return (
      <div>
        <div className="flex items-center gap-2">
          <span>{label}</span>
          <Badge variant="destructive">aucun signal</Badge>
        </div>
      </div>
    );
  }
  const ageH = (Date.now() - new Date(run.at).getTime()) / 3_600_000;
  const ageLabel = ageH >= 1 ? `${Math.round(ageH)} h` : `${Math.max(1, Math.round(ageH * 60))} min`;
  return (
    <div>
      <div className="flex items-center gap-2">
        <span>{label}</span>
        {ageH > staleHours ? (
          <Badge variant="destructive">en retard ({ageLabel})</Badge>
        ) : run.quota_hit ? (
          <Badge variant="secondary">quota atteint</Badge>
        ) : (
          <Badge variant="outline">ok</Badge>
        )}
      </div>
      <div className="text-xs text-muted-foreground">
        {detail(run)} — {new Date(run.at).toLocaleString()}
      </div>
    </div>
  );
}

export default function GeoResolutionPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<GeoStats | null>(null);
  const [items, setItems] = useState<GeocodeCacheItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const size = 25;
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setStats(await getGeoStats(navigate));
    } catch {
      /* non-blocking — the cache table error banner covers connectivity */
    }
  }, [navigate]);

  const loadCache = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listGeocodeCache(navigate, { page, size, q, status });
      setItems(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate, page, q, status]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);
  useEffect(() => {
    loadCache();
  }, [loadCache]);

  const submitSearch = () => {
    setPage(1);
    setQ(qInput.trim());
  };

  const refresh = () => {
    loadStats();
    loadCache();
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-lg font-semibold">Cache de résolution géo</h1>
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCcw className={`mr-1 h-3 w-3 ${loading ? "animate-spin" : ""}`} />
              Rafraîchir
            </Button>
          </div>
        </header>

        <main className="flex flex-col gap-6 p-6">
          {/* KPI cards */}
          <section className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Lieux (geo_locations)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <div className="text-2xl font-semibold">
                  {stats ? `${stats.geo_locations.geocoded} / ${stats.geo_locations.total}` : "…"}
                </div>
                <div className="text-muted-foreground">
                  géocodés{" "}
                  {stats ? `(${pct(stats.geo_locations.geocoded, stats.geo_locations.total)})` : ""}
                </div>
                {stats && stats.geo_locations.pending != null ? (
                  <div className="text-xs text-muted-foreground">
                    {stats.geo_locations.pending} à géocoder ·{" "}
                    {stats.geo_locations.unnamed ?? 0} sans nom (ingéocodables) ·{" "}
                    {stats.geo_locations.exhausted ?? 0} abandonnés
                  </div>
                ) : (
                  stats &&
                  stats.geo_locations.missing > 0 && (
                    <div className="text-xs text-muted-foreground">
                      {stats.geo_locations.missing} sans coordonnées
                    </div>
                  )
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Cache (geocode_cache)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <div className="text-2xl font-semibold">
                  {stats ? stats.geocode_cache.total : "…"}
                </div>
                <div className="text-muted-foreground">
                  {stats
                    ? stats.geocode_cache.true_misses != null
                      ? `${stats.geocode_cache.hits} résolus · ${stats.geocode_cache.country_only ?? 0} pays seul · ${stats.geocode_cache.true_misses} sans résultat`
                      : `${stats.geocode_cache.hits} résolus · ${stats.geocode_cache.misses} sans résultat`
                    : ""}
                </div>
                {stats?.geocode_cache.last_geocoded_at && (
                  <div className="text-xs text-muted-foreground">
                    Dernier : {new Date(stats.geocode_cache.last_geocoded_at).toLocaleString()}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Couverture candidats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <div className="text-2xl font-semibold">
                  {stats ? `${stats.candidates.with_coords} / ${stats.candidates.total}` : "…"}
                </div>
                <div className="text-muted-foreground">
                  avec coordonnées{" "}
                  {stats ? `(${pct(stats.candidates.with_coords, stats.candidates.total)})` : ""}
                </div>
                {stats && (
                  <div className="text-xs text-muted-foreground">
                    {stats.candidates.with_geo_urn} avec geo_urn
                  </div>
                )}
                {stats && stats.candidates.country_mismatch != null && (
                  <div
                    className={
                      stats.candidates.country_mismatch > 0
                        ? "text-xs font-medium text-destructive"
                        : "text-xs text-muted-foreground"
                    }
                    title="Candidats dont le pays géocodé contredit le code pays renvoyé par LinkedIn pour le même profil. Les deux viennent de la même charge utile : ils ne peuvent pas diverger légitimement."
                  >
                    {stats.candidates.country_mismatch} pays incohérent(s)
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Pipeline de résolution</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <PipelineRow
                  label="Backfill nocturne"
                  run={stats?.pipeline?.backfill}
                  staleHours={30}
                  detail={(r) =>
                    `${r.geo_resolved ?? 0} lieu(x) résolu(s) / ${r.geo_pending_before ?? 0} en attente · ${r.candidates_updated ?? 0} candidat(s) mis à jour`
                  }
                />
                <PipelineRow
                  label="Sweeper (5 min)"
                  run={stats?.pipeline?.sweeper}
                  staleHours={0.5}
                  detail={(r) => `${r.resolved ?? 0} résolu(s) / ${r.attempted ?? 0} tenté(s)`}
                />
              </CardContent>
            </Card>
          </section>

          {/* Cache table */}
          <section>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <h2 className="text-base font-medium">Entrées du cache</h2>
              <Badge variant="secondary">{total}</Badge>
              <div className="ml-auto flex items-center gap-2">
                <select
                  className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                  value={status}
                  onChange={(e) => {
                    setPage(1);
                    setStatus(e.target.value);
                  }}
                >
                  <option value="all">Tous</option>
                  <option value="resolved">Résolus</option>
                  <option value="country_only">Pays seul</option>
                  <option value="miss">Sans résultat</option>
                </select>
                <Input
                  className="w-56"
                  placeholder="Rechercher un lieu…"
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitSearch()}
                />
                <Button variant="outline" size="sm" onClick={submitSearch}>
                  <Search className="h-3 w-3" />
                </Button>
              </div>
            </div>

            {error && (
              <div className="mb-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lieu (brut)</TableHead>
                    <TableHead>Ville</TableHead>
                    <TableHead>Région</TableHead>
                    <TableHead>Pays</TableHead>
                    <TableHead>Lat</TableHead>
                    <TableHead>Lon</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Tentatives</TableHead>
                    <TableHead>Géocodé le</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => {
                    const st = itemStatus(it);
                    return (
                      <TableRow key={it.raw_location}>
                        <TableCell className="font-mono text-xs">{it.raw_location}</TableCell>
                        <TableCell>{it.city ?? "—"}</TableCell>
                        <TableCell>{it.region ?? "—"}</TableCell>
                        <TableCell>{it.country ?? "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{fmtCoord(it.latitude)}</TableCell>
                        <TableCell className="font-mono text-xs">{fmtCoord(it.longitude)}</TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[st]}>{STATUS_LABEL[st]}</Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          {st === "miss" ? (it.attempts ?? "—") : "—"}
                        </TableCell>
                        <TableCell className="text-xs">
                          {it.geocoded_at ? new Date(it.geocoded_at).toLocaleString() : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {items.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground">
                        Aucune entrée.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mt-3 flex items-center justify-end gap-3 text-sm">
              <span className="text-muted-foreground">
                Page {page} / {pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </Button>
            </div>
          </section>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
